import type { PropInstance } from "../document/types.ts";
import { artHeight, artLeft, artTop, artWidth } from "./art-box.ts";
import type { FrameSize } from "./prop-hit.ts";

/**
 * Whether the place around a character is dense enough for a cone of sight to act (ADR-061's amendment for P1.9-T82):
 * the cone belongs in a forest, not beside a lone tree. The measure is the cover of a square around the character, the
 * summed area of the art boxes of the props the cone could dim inside it over the square's area; it passes 1 where
 * canopies overlap. A place turns dense at one threshold and open below a lower one, and the cone's strength eases
 * between 0 and 1, so walking along a forest's edge neither flickers nor pops. The game decides the numbers and when
 * to measure; this module only measures and steps.
 */

/**
 * Where a prop stands and how it is mirrored: all the geometry the art box reads from it.
 */
type Placement = Pick<PropInstance, "x" | "y" | "flipX" | "flipY">;

/**
 * The hysteresis a place's density follows: dense at `onAt` cover or more, open again below `offBelow`.
 */
export interface DenseRule {
  readonly onAt: number;
  readonly offBelow: number;
}

/**
 * The area, in square pixels, of a prop's art box that lies inside a rectangle: its art bounds above its base,
 * mirrored with its flips, as `propArtMeetsCone` places them; without art bounds the whole frame width above the base
 * stands in. A rectangle that only touches the box holds none of it.
 *
 * @param prop The prop's anchor and flips.
 * @param size Its frame.
 * @param tileSize Tile size in pixels.
 * @param left The rectangle's left edge in world pixels.
 * @param top Its top edge.
 * @param right Its right edge.
 * @param bottom Its bottom edge.
 * @returns The shared area, 0 when they share none.
 */
export function propArtWithin(prop: Placement, size: FrameSize, tileSize: number, left: number, top: number, right: number, bottom: number): number {
  const artX = artLeft(prop, size, tileSize);
  const width = Math.min(right, artX + artWidth(size)) - Math.max(left, artX);
  if (width <= 0) {
    return 0;
  }
  const artY = artTop(prop, size, tileSize);
  const height = Math.min(bottom, artY + artHeight(size)) - Math.max(top, artY);
  return height <= 0 ? 0 : width * height;
}

/**
 * Whether a place is dense now: an open place turns dense at the rule's `onAt` cover or more, and a dense one stays so
 * until its cover falls below `offBelow`.
 *
 * @param was Whether the place was dense at the last measurement.
 * @param cover The cover measured now.
 * @param rule The hysteresis.
 * @returns Whether it is dense now.
 */
export function denseNext(was: boolean, cover: number, rule: DenseRule): boolean {
  return cover >= (was ? rule.offBelow : rule.onAt);
}

/**
 * The cone's strength stepped toward 1 in a dense place or 0 in an open one, linearly, the whole way taking `easeMs`;
 * it turns round from where it is when the place changes mid-ease.
 *
 * @param from The strength now, in `[0, 1]`.
 * @param dense Whether the place is dense.
 * @param elapsedMs Time since the last step.
 * @param easeMs The time the whole way takes; 0 snaps.
 * @returns The stepped strength, in `[0, 1]`.
 */
export function easeStrength(from: number, dense: boolean, elapsedMs: number, easeMs: number): number {
  const target = dense ? 1 : 0;
  if (easeMs <= 0) {
    return target;
  }
  const step = Math.max(0, elapsedMs) / easeMs;
  return dense ? Math.min(target, from + step) : Math.max(target, from - step);
}

/**
 * The alpha a prop draws at for the cone: its own cone alpha (1 outside the cone, easing toward the cone's alpha
 * inside it) brought toward 1 as the cone's strength falls, so at no strength every prop is whole and at full strength
 * the cone alpha stands exactly.
 *
 * @param dim The prop's cone alpha, in `(0, 1]`.
 * @param strength The cone's strength, in `[0, 1]`.
 * @returns The alpha.
 */
export function coneAlpha(dim: number, strength: number): number {
  return strength >= 1 ? dim : 1 - strength * (1 - dim);
}
